import { clientEnvironment } from './environment';
import { clickIds, identityState, mysiteAttribution, utmParams } from './identity';
import { localTimeParts } from './time';
import { sendEvent, uuid } from './transport';
import type { PageViewEvent, SiteConfig } from './types';

let lastPath: string | null = null;
// Referrer chaining: first page uses document.referrer, SPA navigations use the
// previous in-session URL (matches Umami's behavior).
let lastUrl: string | null = null;

export function trackPageViews(config: SiteConfig): void {
  sendPageView(config, 'load');
  patchHistory(() => sendPageView(config, 'spa'));
  window.addEventListener('popstate', () => sendPageView(config, 'spa'));
}

function sendPageView(config: SiteConfig, navigationType: PageViewEvent['navigation_type']): void {
  const path = window.location.pathname + window.location.search;
  // SPA frameworks fire pushState/replaceState often; only emit on real path changes.
  if (navigationType === 'spa' && path === lastPath) return;

  const referrer = navigationType === 'spa' ? lastUrl : document.referrer || null;
  lastPath = path;
  lastUrl = window.location.href;

  const now = new Date();
  const identity = identityState(now);
  const env = clientEnvironment();

  const event: PageViewEvent = {
    schema_version: 1,
    event_type: 'page_view',
    event_id: uuid(),
    source: 'embed',
    site_id: config.siteId,
    org_id: config.orgId,
    location_id: config.locationId,
    restaurant_country: config.restaurantCountry,
    restaurant_city: config.restaurantCity,
    visitor_id: identity.visitor_id,
    session_id: identity.session_id,
    session_started_at_utc: identity.session_started_at_utc,
    source_url: window.location.href,
    hostname: env.hostname,
    path,
    referrer,
    page_title: document.title || null,
    screen: env.screen,
    language: env.language,
    navigation_type: navigationType,
    mysite_attribution: mysiteAttribution(),
    utm: utmParams(),
    click_ids: clickIds(),
    ...localTimeParts(now, config.timezone),
  };

  sendEvent(config.collectUrl, event);
}

type HistoryMethod = 'pushState' | 'replaceState';

function patchHistory(onChange: () => void): void {
  for (const method of ['pushState', 'replaceState'] as HistoryMethod[]) {
    const original = history[method];
    history[method] = function patched(this: History, ...args: Parameters<History[HistoryMethod]>) {
      const result = original.apply(this, args);
      try {
        onChange();
      } catch {
        // never let analytics break navigation
      }
      return result;
    };
  }
}
